import * as API from 'aws-amplify/api';
import * as Sentry from '@sentry/react';
import { ensureAmplifyConfigured, initApp } from 'utils/config/initApiConfig';
import { Amplify } from 'aws-amplify';
import { debounce } from 'lodash';
interface ApiRequestOptions {
    headers?: Record<string, string>;
    queryParams?: Record<string, string | number | boolean>;
    body?: any;
}
const is_prod = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_APP_STAGE?.trim() === 'prod') ||
    (typeof process !== 'undefined' && (process as any).env?.VITE_APP_STAGE?.trim() === 'prod');
const isLocal = (): boolean => {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.1.');
};



const getLocalAuthHeaders = (): Record<string, string> => {
    if (!isLocal()||is_prod) return {};
    const isProd = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_APP_STAGE?.trim() === 'prod') ||
        (typeof process !== 'undefined' && (process as any).env?.VITE_APP_STAGE?.trim() === 'prod');
    const token = isProd ?
        (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_X_AUTH_TOKEN_PROD) ||
        (typeof process !== 'undefined' && (process as any).env?.VITE_X_AUTH_TOKEN_PROD) ||
        '' :
        (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_X_AUTH_TOKEN_DEV) ||
        (typeof process !== 'undefined' && (process as any).env?.VITE_X_AUTH_TOKEN_DEV) ||
        '';
    if (isProd && !token) {
        return {};
    }
    if (!token) return {};
    return { 'x-auth-token': token };
};

const mergeOptions = (options?: ApiRequestOptions): ApiRequestOptions | undefined => {
    const localHeaders = getLocalAuthHeaders();
    if (Object.keys(localHeaders).length === 0) return options;
    return {
        ...options,
        headers: { ...localHeaders, ...(options?.headers||{}) },
    };
    // return options;
};

class NetworkUnavailableError extends Error {
    constructor(message: string = 'Network Error') {
        super(message);
        this.name = 'NetworkUnavailableError';
        this.message = message;
    }
}

const checkNetworkAvailability = (): void => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new NetworkUnavailableError('Network Error');
    }
};

/**
 * Converts queryParams to string-only values to meet Amplify's typing.
 */
const normalizeQueryParams = (
    queryParams?: Record<string, string | number | boolean>
): Record<string, string> | undefined => {
    if (!queryParams) return undefined;

    const result: Record<string, string> = {};
    for (const key in queryParams) {
        if (Object.prototype.hasOwnProperty.call(queryParams, key)) {
            result[key] = String(queryParams[key]);
        }
    }
    return result;
};

/**
 * Safely parses response body as JSON.
 */
const getJsonData = async (body: any): Promise<any | null> => {
    if (body?.json) {
        try {
            return await body.json();
        } catch (error) {
            return null;
        }
    }
    return null;
};

/**
 * Validates API name and captures InvalidApiName errors with full context
 */
const logConfigLostDebounce = debounce((apiName: string, operation: string, path: string, get_config: any) => {
    const errorContext = {
        apiName,
        operation,
        path,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        get_config
    };
    Sentry.captureException(new Error(`Initial configuration not found for api ${apiName} ${operation} operation`), {
        tags: {
            errorType: 'Initial configuration not found',
            operation,
            apiName: apiName || 'undefined'
        },
        extra: errorContext,
        level: 'error'
    });
}, 1000)
const validateApiName = (apiName: string, operation: string, path: string): void => {
    const get_config = Amplify.getConfig() || {};
    if(!get_config || typeof get_config !== 'object'|| Object.keys(get_config).length === 0 || !get_config.API || !get_config.API.REST) {
        initApp()
        logConfigLostDebounce(apiName, operation, path , get_config)
    }
    if (!apiName || typeof apiName !== 'string' || apiName.trim() === '') {
        const errorContext = {
            apiName,
            operation,
            path,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        };
        
        Sentry.captureException(new Error(`InvalidApiName: API name is invalid for ${operation} operation`), {
            tags: {
                errorType: 'InvalidApiName',
                operation,
                apiName: apiName || 'undefined'
            },
            extra: errorContext,
            level: 'error'
        });
        
        throw new Error(`InvalidApiName: API name is invalid for ${operation} operation. Received: ${apiName}`);
    }
    
    if (path !== undefined && path !== null && typeof path !== 'string') {
        const errorContext = {
            apiName,
            operation,
            path,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        };
        
        Sentry.captureException(new Error(`InvalidPath: Path must be a string for ${operation} operation`), {
            tags: {
                errorType: 'InvalidPath',
                operation,
                apiName
            },
            extra: errorContext,
            level: 'error'
        });
        
        throw new Error(`InvalidPath: Path must be a string for ${operation} operation. Received: ${typeof path}`);
    }
};

/**
 * Handles API errors with comprehensive Sentry logging
 */
const handleApiError = (error: any, apiName: string, operation: string, path: string, options?: ApiRequestOptions): never => {
    if (error instanceof NetworkUnavailableError) {
        throw error;
    }

    const errorContext = {
        apiName,
        operation,
        path,
        options: {
            headers: options?.headers,
            queryParams: options?.queryParams,
            hasBody: !!options?.body
        },
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        errorMessage: error?.message || 'Unknown error',
        errorStack: error?.stack
    };

    if (error?.message?.includes('InvalidApiName')) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'InvalidApiName',
                operation,
                apiName
            },
            extra: errorContext,
            level: 'error'
        });
    } 
    
    throw error;
};

export const apis = {
    get: async (apiName: string, path: string, options?: ApiRequestOptions): Promise<any | null> => {
        try {
            checkNetworkAvailability();
            await ensureAmplifyConfigured();
            validateApiName(apiName, 'GET', path);
            
            const result = await API.get({
                apiName,
                path,
                options: {
                    ...mergeOptions(options),
                    queryParams: normalizeQueryParams(options?.queryParams),
                },
            }).response;
            return await getJsonData(result.body);
        } catch (error) {
            handleApiError(error, apiName, 'GET', path, options);
        }
    },

    post: async (apiName: string, path: string, options?: ApiRequestOptions): Promise<any | null> => {
        try {
            checkNetworkAvailability();
            await ensureAmplifyConfigured();
            validateApiName(apiName, 'POST', path);
            const mergedOptions = mergeOptions(options);
            const result = await API.post({
                apiName,
                path,
                options: {
                    ...mergedOptions,
                    queryParams: normalizeQueryParams(options?.queryParams),
                },
            }).response;
            return await getJsonData(result.body);
        } catch (error) {
            handleApiError(error, apiName, 'POST', path, options);
        }
    },

    put: async (apiName: string, path: string, options?: ApiRequestOptions): Promise<any | null> => {
        try {
            checkNetworkAvailability();
            await ensureAmplifyConfigured();
            validateApiName(apiName, 'PUT', path);
            
            const result = await API.put({
                apiName,
                path,
                options: {
                    ...mergeOptions(options),
                    queryParams: normalizeQueryParams(options?.queryParams),
                },
            }).response;
            return await getJsonData(result.body);
        } catch (error) {
            handleApiError(error, apiName, 'PUT', path, options);
        }
    },

    del: async (apiName: string, path: string, options?: ApiRequestOptions): Promise<any | null> => {
        try {
            checkNetworkAvailability();
            await ensureAmplifyConfigured();
            validateApiName(apiName, 'DELETE', path);
            
            const result = await API.del({
                apiName,
                path,
                options: {
                    ...mergeOptions(options),
                    queryParams: normalizeQueryParams(options?.queryParams),
                },
            }).response;
            return await getJsonData((result as unknown as { body: any }).body);
        } catch (error) {
            handleApiError(error, apiName, 'DELETE', path, options);
        }
    },
};